import axiosInstance from '@/lib/enhancedAxiosInstance';
import { API_ENDPOINTS } from '@/config/config';
import { logError } from '@/utils/errorHandler';

export interface ImportTask {
  id: number;
  status: string;
  created_at: string;
  csv_file?: string;
  total_rows?: number;
  processed?: number;
  error_file?: string;
  error_file_url?: string;
  error_count?: number;
  import_type?: string;
  uploaded_files_count?: number;
  matched_products_count?: number;
  images_created_count?: number;
}

export interface ImportFieldSchemaEntry {
  id: string;
  label: string;
  required: boolean;
  type: string;
}

export interface FieldSchemaResponse {
  fields: ImportFieldSchemaEntry[];
  attribute_header_pattern?: string;
}

export type DuplicateStrategy = 'skip' | 'overwrite' | 'abort';

export interface ImportOptions {
  overwrite_policy?: DuplicateStrategy;
}

export interface UploadProgressEvent {
  loaded: number;
  total: number;
  percentage: number;
}

// Enhanced import types
export interface ImportResult {
  status: 'success' | 'partial' | 'failed';
  imported_count: number;
  skipped_count: number;
  total_rows: number;
  error_count: number;
  warning_count: number;
  errors: ImportError[];
  warnings: ImportWarning[];
  header_validation?: HeaderValidationResult;
}

export interface ImportError {
  row: number;
  column: string;
  message: string;
  attribute?: string;
}

export interface ImportWarning {
  row: number;
  column: string;
  message: string;
  attribute?: string;
}

export interface HeaderValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  header_map: Record<string, any>;
}

export interface AttributeTypeConfig {
  description: string;
  example_value: any;
  csv_format: string;
  supports_validation_rule: boolean;
  supports_locale: boolean;
  supports_channel: boolean;
}

export interface MeasurementUnitsResponse {
  units_by_category: Record<string, string[]>;
  all_units: string[];
}

/**
 * Enterprise-grade file validation that works in both browser and test environments
 * Checks multiple properties to ensure the object is a valid File-like object
 */
const isValidFileObject = (file: any): file is File => {
  if (!file) return false;

  // Check for File constructor in browser environment
  if (typeof File !== 'undefined' && file instanceof File) {
    return true;
  }

  // Enhanced check for File-like properties for test environments
  const hasFileProperties =
    typeof file === 'object' &&
    typeof file.name === 'string' &&
    typeof file.size === 'number' &&
    typeof file.type === 'string' &&
    (typeof file.lastModified === 'number' || typeof file.lastModifiedDate === 'object') &&
    // Check for Blob methods/properties
    (typeof file.slice === 'function' ||
      typeof file.stream === 'function' ||
      file.constructor?.name === 'Blob' ||
      file.constructor?.name === 'File' ||
      file.constructor?.name === 'FilePolyfill');

  // Additional check for test environment File-like objects
  const isTestFile =
    hasFileProperties &&
    // Allow test-created files that have basic file properties
    file.name &&
    file.size >= 0 &&
    file.type;

  // Check for Symbol.toStringTag
  const hasFileSymbol = file[Symbol.toStringTag] === 'File';

  return hasFileProperties || isTestFile || hasFileSymbol;
};

export const createProductImport = async (
  file: File,
  mapping: Record<string, string>,
  duplicateStrategy: DuplicateStrategy = 'overwrite',
  onProgress?: (progress: UploadProgressEvent) => void
) => {
  if (!isValidFileObject(file)) {
    throw new Error(
      'The file is not a real File object. Please re-select the file before uploading.'
    );
  }
  const fd = new FormData();
  fd.append('csv_file', file, file.name);
  fd.append('mapping', JSON.stringify(mapping));
  fd.append('duplicate_strategy', duplicateStrategy);

  return axiosInstance.post<ImportTask>(API_ENDPOINTS.imports.create, fd, {
    onUploadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage,
        });
      }
    },
  });
};

// Enhanced import function
export const createEnhancedProductImport = async (
  file: File,
  importMode: 'update_or_create' | 'create_only' | 'update_only' = 'update_or_create',
  strictMode: boolean = true,
  onProgress?: (progress: UploadProgressEvent) => void
) => {
  if (!isValidFileObject(file)) {
    throw new Error(
      'The file is not a real File object. Please re-select the file before uploading.'
    );
  }

  const fd = new FormData();
  fd.append('file', file, file.name);
  fd.append('import_mode', importMode);
  fd.append('strict_mode', strictMode.toString());

  return axiosInstance.post<ImportResult>(`${API_ENDPOINTS.imports.create}upload-enhanced/`, fd, {
    onUploadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage,
        });
      }
    },
  });
};

// Header validation
export const validateCSVHeaders = async (headers: string[]) => {
  return axiosInstance.post<HeaderValidationResult>(
    `${API_ENDPOINTS.imports.create}validate-headers/`,
    {
      headers,
    }
  );
};

// SKU duplicate validation
export interface SkuDuplicateCheckResult {
  duplicates: string[];
  error?: string;
}

export const checkSkuDuplicates = async (skus: string[]): Promise<SkuDuplicateCheckResult> => {
  try {
    // Filter out empty SKUs before sending
    const validSkus = skus.filter(sku => sku && sku.trim().length > 0);

    if (validSkus.length === 0) {
      return { duplicates: [] };
    }

    const response = await axiosInstance.post<SkuDuplicateCheckResult>('/api/sku-check/', {
      skus: validSkus,
    });

    return response.data;
  } catch (error: any) {
    logError(
      'Failed to check SKU duplicates',
      {
        component: 'importService',
        action: 'checkSkuDuplicates',
        metadata: { skus, error },
      },
      { showToast: false, userFacing: false }
    );

    // Provide more specific error messages based on the error type
    let errorMessage = 'Failed to check for duplicate SKUs';

    if (error?.response?.status === 401) {
      errorMessage = 'Authentication required to check SKUs';
    } else if (error?.response?.status === 403) {
      errorMessage = 'Permission denied to check SKUs';
    } else if (error?.response?.status === 404) {
      errorMessage = 'SKU check endpoint not found';
    } else if (error?.response?.status >= 500) {
      errorMessage = 'Server error while checking SKUs';
    } else if (error?.code === 'NETWORK_ERROR' || !error?.response) {
      errorMessage = 'Network error - unable to reach server';
    } else if (error?.response?.data?.error) {
      errorMessage = error.response.data.error;
    }

    return {
      duplicates: [],
      error: errorMessage,
    };
  }
};

// Download CSV template
export const downloadCSVTemplate = async (
  includeAllAttributes: boolean = true,
  familyCode?: string,
  format: 'csv' | 'xlsx' = 'csv'
) => {
  const params = new URLSearchParams({
    include_all_attributes: includeAllAttributes.toString(),
    format,
  });

  if (familyCode) {
    params.append('family_code', familyCode);
  }

  return axiosInstance.get(`${API_ENDPOINTS.imports.create}template/?${params.toString()}`, {
    responseType: 'blob',
  });
};

// Get attribute type configurations
export const getAttributeTypeConfigurations = async () => {
  return axiosInstance.get<Record<string, AttributeTypeConfig>>(
    `${API_ENDPOINTS.imports.create}attribute-config/`
  );
};

// Get measurement units
export const getMeasurementUnits = async () => {
  return axiosInstance.get<MeasurementUnitsResponse>(
    `${API_ENDPOINTS.imports.create}measurement-units/`
  );
};

// Validate single attribute value
export const validateAttributeValue = async (
  attributeId: number,
  value: any,
  locale?: string,
  channel?: string
) => {
  return axiosInstance.post(`${API_ENDPOINTS.imports.create}validate-attribute-value/`, {
    attribute_id: attributeId,
    value,
    locale,
    channel,
  });
};

export const createAttributeGroupImport = async (
  file: File,
  mapping: Record<string, string>,
  onProgress?: (progress: UploadProgressEvent) => void
) => {
  if (!isValidFileObject(file)) {
    throw new Error(
      'The file is not a real File object. Please re-select the file before uploading.'
    );
  }
  const fd = new FormData();
  fd.append('csv_file', file, file.name);
  fd.append('mapping', JSON.stringify(mapping));

  return axiosInstance.post<ImportTask>(API_ENDPOINTS.imports.attributeGroups, fd, {
    onUploadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage,
        });
      }
    },
  });
};

export const createAttributeImport = async (
  file: File,
  mapping: Record<string, string>,
  onProgress?: (progress: UploadProgressEvent) => void
) => {
  if (!isValidFileObject(file)) {
    throw new Error(
      'The file is not a real File object. Please re-select the file before uploading.'
    );
  }
  const fd = new FormData();
  fd.append('csv_file', file, file.name);
  fd.append('mapping', JSON.stringify(mapping));

  return axiosInstance.post<ImportTask>(API_ENDPOINTS.imports.attributes, fd, {
    onUploadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage,
        });
      }
    },
  });
};

export const createFamilyImport = async (
  file: File,
  mapping: Record<string, string>,
  onProgress?: (progress: UploadProgressEvent) => void
) => {
  if (!isValidFileObject(file)) {
    throw new Error(
      'The file is not a real File object. Please re-select the file before uploading.'
    );
  }
  const fd = new FormData();
  fd.append('csv_file', file, file.name);
  fd.append('mapping', JSON.stringify(mapping));

  return axiosInstance.post<ImportTask>(API_ENDPOINTS.imports.families, fd, {
    onUploadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage,
        });
      }
    },
  });
};

export const createBulkImageImport = async (
  images?: File[],
  zipFile?: File,
  overwriteExisting = false,
  onProgress?: (progress: UploadProgressEvent) => void
) => {
  // Validate image files if provided
  if (images && images.length > 0) {
    images.forEach((image, index) => {
      if (!isValidFileObject(image)) {
        throw new Error(
          `Image file at index ${index} is not a valid File object. Please re-select the files.`
        );
      }
    });
  }

  // Validate zip file if provided
  if (zipFile && !isValidFileObject(zipFile)) {
    throw new Error('The ZIP file is not a valid File object. Please re-select the file.');
  }

  const fd = new FormData();

  if (images && images.length > 0) {
    images.forEach(image => {
      fd.append('images', image);
    });
  }

  if (zipFile) {
    fd.append('zip_file', zipFile);
  }

  fd.append('overwrite_existing', overwriteExisting.toString());

  return axiosInstance.post<ImportTask>(API_ENDPOINTS.imports.bulkImages, fd, {
    onUploadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage,
        });
      }
    },
  });
};

export const getImportDetails = (id: number) => {
  return axiosInstance.get<ImportTask>(API_ENDPOINTS.imports.details(id));
};

// Helper function to get the correct import function based on mode and structure type
export const getImportFunction = (importMode: string, structureType?: string) => {
  if (importMode === 'products') {
    return createProductImport;
  } else if (importMode === 'structure') {
    switch (structureType) {
      case 'attribute_groups':
        return createAttributeGroupImport;
      case 'attributes':
        return createAttributeImport;
      case 'families':
        return createFamilyImport;
      default:
        throw new Error(`Unknown structure type: ${structureType}`);
    }
  } else {
    throw new Error(`Unknown import mode: ${importMode}`);
  }
};

// Helper function to get import type name for error handling
export const getImportTypeName = (importMode: string, structureType?: string): string => {
  if (importMode === 'products') {
    return 'products';
  } else if (importMode === 'structure' && structureType) {
    return structureType;
  }
  return 'import';
};

export const getImportFieldSchema = (version?: number) => {
  const url =
    version === 2 ? API_ENDPOINTS.imports.fieldSchemaV2 : API_ENDPOINTS.imports.fieldSchema;
  return axiosInstance.get<FieldSchemaResponse>(url);
};

export const getAttributeGroupSchemaFields = () =>
  axiosInstance.get<ImportFieldSchemaEntry[]>(API_ENDPOINTS.imports.attributeGroupsSchema);

export const getAttributeSchemaFields = () =>
  axiosInstance.get<ImportFieldSchemaEntry[]>(API_ENDPOINTS.imports.attributesSchema);

export const getFamilySchemaFields = () =>
  axiosInstance.get<ImportFieldSchemaEntry[]>(API_ENDPOINTS.imports.familiesSchema);

export const getFamilyAttributes = (familyCode: string) =>
  axiosInstance.get(API_ENDPOINTS.imports.familyAttributes(familyCode));
